import React, {useCallback, useEffect, useRef, useState} from "react";
import {Alert, Badge, Button, Form, Spinner, Table} from "react-bootstrap";
import DateRangeSelector from "../DateRangeSelector";
import {listJobComplaints, normalizeComplaint, updateComplaintAccounting} from "../../service/JobComplaintService";

function initialRange() {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 365);
    return {startDate, endDate, key: "selection"};
}

export default function ComplaintAccountingTab() {
    const [range, setRange] = useState(initialRange);
    const [status, setStatus] = useState("PENDING");
    const [rows, setRows] = useState([]);
    const [credits, setCredits] = useState({});
    const [loading, setLoading] = useState(false);
    const [savingIds, setSavingIds] = useState(() => new Set());
    const [message, setMessage] = useState(null);
    const requestId = useRef(0);
    const statusRef = useRef(status);
    const savingLock = useRef(false);
    statusRef.current = status;

    const load = useCallback(async () => {
        const currentRequest = ++requestId.current;
        setLoading(true);
        setMessage(null);
        try {
            const response = await listJobComplaints(range.startDate, range.endDate, true, status || undefined);
            if (!response.ok) throw new Error(await response.text() || "Unable to load complaint deductions");
            const data = (await response.json()).map(normalizeComplaint);
            if (currentRequest === requestId.current) {
                setRows(data);
                setCredits(Object.fromEntries(data.map(item => [item.id, String(item.creditPrice ?? 0)])));
            }
        } catch (err) {
            if (currentRequest === requestId.current) setMessage({variant: "danger", text: err.message});
        } finally {
            if (currentRequest === requestId.current) setLoading(false);
        }
    }, [range, status]);

    useEffect(() => { load(); }, [load]);

    async function changeStatus(item, nextStatus) {
        if (savingLock.current) return;
        savingLock.current = true;
        setSavingIds(current => new Set(current).add(item.id));
        setMessage(null);
        try {
            const response = await updateComplaintAccounting(item, credits[item.id], nextStatus);
            if (!response.ok) throw new Error(await response.text() || "Unable to update accounting");
            const updated = normalizeComplaint(await response.json());
            setRows(current => {
                if (statusRef.current && statusRef.current !== nextStatus) {
                    return current.filter(row => row.id !== item.id);
                }
                return current.map(row => row.id === item.id ? updated : row);
            });
            setMessage({variant: "success", text: nextStatus === "COMPLETED" ? "Complaint accounting completed." : "Complaint accounting reopened."});
        } catch (err) {
            setMessage({variant: "danger", text: err.message});
        } finally {
            savingLock.current = false;
            setSavingIds(current => {
                const next = new Set(current);
                next.delete(item.id);
                return next;
            });
        }
    }

    return <div className="p-3">
        <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
            <DateRangeSelector startDate={range.startDate} endDate={range.endDate} onDateChange={event => setRange(event.selection)}/>
            <Form.Select aria-label="Accounting status" value={status} onChange={event => setStatus(event.target.value)} style={{width: 170}}>
                <option value="PENDING">Pending</option>
                <option value="COMPLETED">Completed</option>
                <option value="">All</option>
            </Form.Select>
            <Button variant="outline-secondary" onClick={load} disabled={loading}>Refresh</Button>
        </div>
        <p className="text-muted">Wage deduction and Job Credit are separate amounts. Complaints with a $0 deduction are excluded.</p>
        {message ? <Alert variant={message.variant}>{message.text}</Alert> : null}
        {loading ? <div className="text-center"><Spinner animation="border"/></div> : <Table responsive striped hover>
            <thead><tr><th>Complaint date</th><th>Job / Worker</th><th>Reason</th><th>Wage deduction</th><th>Job Credit</th><th>Status</th><th>Action</th></tr></thead>
            <tbody>{rows.map(item => <tr key={item.id}>
                <td>{new Date(item.complainedAt).toLocaleString()}</td>
                <td><b>{item.cosJobId}</b><br/>{item.workerName}<br/><small>{item.fullAddress}</small></td>
                <td style={{whiteSpace: "pre-wrap", minWidth: 220}}>{item.reason}</td>
                <td><b>${Number(item.deductionAmount).toFixed(2)}</b></td>
                <td><Form.Control aria-label={`Credit for ${item.cosJobId}`} type="number" step="0.01" value={credits[item.id] ?? "0"} onChange={event => setCredits({...credits, [item.id]: event.target.value})}/></td>
                <td><Badge bg={item.accountingStatus === "COMPLETED" ? "success" : "warning"}>{item.accountingStatus}</Badge></td>
                <td>{item.accountingStatus === "COMPLETED"
                    ? <Button size="sm" variant="outline-secondary" disabled={savingIds.size > 0} onClick={() => changeStatus(item, "PENDING")}>Reopen</Button>
                    : <Button size="sm" variant="success" disabled={savingIds.size > 0} onClick={() => changeStatus(item, "COMPLETED")}>{savingIds.has(item.id) ? "Saving…" : "Mark complete"}</Button>}
                </td>
            </tr>)}</tbody>
        </Table>}
        {!loading && !rows.length ? <p className="text-muted text-center">No complaint deductions match the filter.</p> : null}
    </div>;
}
